import {
  AssetType,
  BorrowerOnboardingDetails,
  CustomTheme,
  getAgentById,
  getLoanApplicationsByLoanId,
} from '@nemuru-eng/js-api-client';

import { loanApplicationsHaveCompanyCifRequiredLender } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/_shared/utils/companyCifRequiredLender';
import { ProfessionalDetailsEditDialog } from '@/components/modules/KYC/KYCLoan/BorrowerKYC/components/ProfessionalDetailsSection/components/ProfessionalDetailsEditDialog';
import { ProfessionalDetailsForm } from '@/components/modules/KYC/KYCLoan/BorrowerKYC/components/ProfessionalDetailsSection/components/ProfessionalDetailsForm';
import { useUpdateBorrowerData } from '@/components/modules/KYC/KYCLoan/BorrowerKYC/hooks/useUpdateBorrowerContract';
import { PendingDataWarning } from '@/components/shared/components/PendingDataWarning';
import { SubSectionWrapper } from '@/components/shared/components/SubSectionWrapper';
import { useDialog } from '@/components/shared/hooks/useDialog';
import { PendingDataType } from '@/config/types';
import { useCache } from '@/hooks/requests/useCache';

interface ProfessionalDetailsSectionProps {
  loanId: string;
  agentId: string;
  onboardingDetails: BorrowerOnboardingDetails;
  editModeAllowed?: boolean;
}

export const ProfessionalDetailsSection = ({
  loanId,
  agentId,
  onboardingDetails,
  editModeAllowed = false,
}: ProfessionalDetailsSectionProps) => {
  const { opened, toggleDialog, setOpened } = useDialog();
  const { loan, updateBorrowerContract, requestLoading } = useUpdateBorrowerData({
    loanId,
    onboardingDetails,
  });

  const { data: agent } = useCache(getAgentById, { agentId });
  const { data: loanApplications } = useCache(getLoanApplicationsByLoanId, { loanId });

  if (!loan || !agent) return null;

  const professionalDetails = 'userDetails' in onboardingDetails && onboardingDetails?.userDetails?.professionalDetails;
  const toggleEditModeDialog = editModeAllowed && professionalDetails ? toggleDialog : undefined;
  const additionalProfessionalDetailsRequired =
    agent.customTheme !== CustomTheme.CAIXABANK && loan.assetReference[0].type === AssetType.AUTO;
  const isCompanyCifRequired =
    additionalProfessionalDetailsRequired && loanApplicationsHaveCompanyCifRequiredLender(loanApplications);

  return (
    <SubSectionWrapper title={'PROFESSIONAL_DETAILS'} toggleEditModeDialog={toggleEditModeDialog}>
      {professionalDetails ? (
        <>
          <ProfessionalDetailsForm
            professionalDetails={professionalDetails}
            additionalProfessionalDetailsRequired={additionalProfessionalDetailsRequired}
            isCompanyCifRequired={isCompanyCifRequired}
          />
          {opened && (
            <ProfessionalDetailsEditDialog
              professionalDetails={professionalDetails}
              loan={loan}
              opened={opened}
              setOpened={setOpened}
              toggleDialog={toggleDialog}
              updateBorrowerContract={updateBorrowerContract}
              loading={requestLoading}
              isCompanyCifRequired={isCompanyCifRequired}
            />
          )}
        </>
      ) : (
        <PendingDataWarning type={PendingDataType.BORROWER_ONBOARDING} />
      )}
    </SubSectionWrapper>
  );
};
